// src/publish/serialize/markdown.js — blocks → Markdown (.md; Ghost, Notion…).
//
// Rules:
//   strong / em     **…** / *…*
//   list item       "- item", numbered "1. item"
//   link            [testo](URL) — the bare URL when the text IS the URL (the
//                   samples write it so; every target that matters autolinks it)
//   heading         #, ##, ### by level
//   br              "\n" inside the paragraph; blocks separated by a blank line
//   cite "[N]"      kept as [N]
//   qa              **question**, blank line, answer blocks
//   slide           "### Slide N · Tipo", **headline**, body, *Indicazione visiva:* …
//   source piece    a fenced ```html block (piecesToMarkdown only)
// No HTML tag is ever written here. Text is NOT escaped for Markdown: this
// output mirrors the prototype's .md samples byte for byte, so a text run that
// opens with "1. ", "- ", "#" or holds "*" / "_" pairs keeps those characters
// and a strict renderer may read them as markup. The .md file is a review
// format (Ghost, Notion); the paste formats are plain text and HTML.

import i18n from '../../i18n/index.js';
import { asRuns, asBlocks, runsToText } from '../blocks.js';
import { slideTitle, listItems } from './plain.js';

/** The Markdown of a run list. */
export function runsToMarkdown(runs) {
  let s = '';
  for (const r of asRuns(runs)) {
    switch (r.type) {
      case 'text': s += r.text; break;
      case 'br': s += '\n'; break;
      case 'cite': s += `[${r.n}]`; break;
      case 'strong': { const t = runsToMarkdown(r.runs); if (t) s += `**${t}**`; break; }
      case 'em': { const t = runsToMarkdown(r.runs); if (t) s += `*${t}*`; break; }
      case 'link': {
        const label = runsToMarkdown(r.runs);
        if (!r.href) s += label;
        else if (!label || label === r.href) s += r.href;
        else s += `[${label}](${r.href})`;
        break;
      }
      default: break;
    }
  }
  return s;
}

function listToMarkdown(block) {
  return listItems(block)
    .map((it, i) => `${block.ordered ? `${i + 1}. ` : '- '}${runsToMarkdown(it)}`)
    .join('\n');
}

function slideToMarkdown(block) {
  const parts = [`### ${slideTitle(block)}`];
  const headline = runsToMarkdown(block.headline);
  if (headline) parts.push(`**${headline}**`);
  const body = blocksToMarkdown(block.body);
  if (body) parts.push(body);
  if (block.visualCue) parts.push(`*${i18n.t('outputs:pieces.visualCue')}:* ${block.visualCue}`);
  return parts.join('\n\n');
}

/** Blocks → Markdown; blocks separated by a blank line. */
export function blocksToMarkdown(blocks) {
  const out = [];
  for (const b of asBlocks(blocks)) {
    switch (b.type) {
      case 'heading': out.push(`${'#'.repeat(b.level)} ${runsToMarkdown(b.runs)}`); break;
      case 'paragraph': out.push(runsToMarkdown(b.runs)); break;
      case 'list': out.push(listToMarkdown(b)); break;
      case 'qa': {
        const q = runsToMarkdown(b.question);
        const a = blocksToMarkdown(b.answer);
        out.push([q ? `**${q}**` : '', a].filter(Boolean).join('\n\n'));
        break;
      }
      case 'slide': out.push(slideToMarkdown(b)); break;
      default: break;
    }
  }
  return out.filter((s) => s !== '').join('\n\n');
}

/** The verbatim text of a 'source' piece (its paragraphs, one per line). */
export function sourceText(piece) {
  return asBlocks(piece?.blocks)
    .map((b) => (b.type === 'paragraph' || b.type === 'heading' ? runsToText(b.runs, { cites: true }) : ''))
    .filter(Boolean)
    .join('\n');
}

/**
 * A whole piece list as one Markdown document: "# title", then a level-2
 * heading with each piece's label followed by its blocks (a 'source' piece as a
 * fenced html block). Ends with a newline.
 */
export function piecesToMarkdown(pieces, { title = '' } = {}) {
  const list = Array.isArray(pieces) ? pieces : [];
  const parts = [];
  if (title) parts.push(`# ${title}`);
  for (const p of list) {
    if (!p) continue;
    const body = p.kind === 'source'
      ? `\`\`\`html\n${sourceText(p)}\n\`\`\``
      : blocksToMarkdown(p.blocks);
    parts.push(`## ${p.label}\n\n${body}`);
  }
  return `${parts.join('\n\n')}\n`;
}
